import { HttpStatusCode } from '@angular/common/http';
import { Injectable, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Observable, Subject } from 'rxjs';
import { Actions, ofType } from '@ngrx/effects';
import { PersonActions } from '@states/person/person.actions';
import { PersonGroupsActions } from '@states/person-groups/person-groups.actions';
import { VehiclesActions } from '@states/vehicles/vehicles.action-types';
import { VehicleGroupsActions } from '@states/vehicle-groups/vehicle-groups.actions';
import { VehicleGroupsModel } from '@models/vehicle-groups.model';
import { PersonGroupsModel } from '@models/person-groups.model';
import { IntelligenceModels } from '../store/models/intelligence.model';
import { PersonService } from './person.service';
import { ArchiveActions } from '@states/archive/archive.action-types';
import { Archive } from '@models/archive.model';
import { OrganizationUsersActions } from '@states/organization-users/organization-users.action-types';
import * as OrganizationActions from '@states/organization/organization.actions';
import { OrganizationModels } from '@models/organization.model';
import { OrgVariablesActions } from '@states/org-variables/org-variables.action-types';
import { OrgVariablesModel } from '@models/org-variables.model';
import { ZoneRegionsModels } from '@share-models';
import { ZoneRegionsActions } from '@states/zone-regions/zone-regions.actions';
import { DatabaseTagsActions } from '@states/database-tags/database-tags.action.types';
import { DatabaseTagsModel } from '@models/database-tags.model';
import { CustomObjectsActions } from '@states/custom-objects/custom-objects.action-types';
import { ScheduleActions } from '@states/schedule/schedule.action-types';
import { CustomObjectStatesActions } from '@states/custom-objects-states/custom-objects-states.action-types';
import { CaseCategoriesActions } from '@states/case-category/case-category.actions';
import { CaseCategoriesModel } from '@models/case-categories.model';

export type IntelligenceResultType =
  | 'organization-members'
  | 'user-organizations'
  | 'tags'
  | 'event-tags'
  | IntelligenceModels.EventTags.PanelResultType.CreateEventTag
  | 'time-schedule'
  | IntelligenceModels.TimeSchedules.PanelResultType.CreateTimeSchedule
  | 'device-health'
  | 'device-list'
  | 'locations'
  | 'persons'
  | 'person-groups'
  | 'dashboards'
  | 'vehicles'
  | 'vehicle-groups'
  | 'search'
  | 'alerts'
  | 'alert-configurations'
  | 'alert-templates'
  | 'create-alert'
  | 'case-manager'
  | 'case-manager-feature'
  | 'case-manager-category'
  | 'archive'
  | IntelligenceModels.Locations.PanelResultType.PickCamera
  | IntelligenceModels.CameraProvisioning.PanelResultType.AddCamera
  | IntelligenceModels.CoreProvisioning.PanelResultType.AddCore
  | 'wall'
  | 'create-wall'
  | 'wall-camera'
  | 'chart'
  | 'variables'
  | 'zone-regions'
  | 'custom-objects';

export type IntelligenceResultData =
  | IntelligenceModels.Organizations.MembersResponse
  | IntelligenceModels.Organizations.UserOrganizationsViewResponse
  | IntelligenceModels.Tags.ListResponse
  | IntelligenceModels.EventTags.ListResponse
  | IntelligenceModels.EventTags.CreateDraft
  | IntelligenceModels.TimeSchedules.ListResponse
  | IntelligenceModels.TimeSchedules.CreateDraft
  | IntelligenceModels.DeviceHealth.Response
  | IntelligenceModels.DeviceList.Response
  | IntelligenceModels.Locations.Response
  | IntelligenceModels.Persons.Response
  | IntelligenceModels.PersonGroups.Response
  | IntelligenceModels.PersonGroups.MembersResponse
  | IntelligenceModels.Dashboards.Response
  | IntelligenceModels.Dashboards.DetailResponse
  | IntelligenceModels.Vehicles.Response
  | IntelligenceModels.VehicleGroups.Response
  | IntelligenceModels.VehicleGroups.MembersResponse
  | IntelligenceModels.Search.Response
  | IntelligenceModels.Alerts.Response
  | IntelligenceModels.AlertConfigurations.Response
  | IntelligenceModels.AlertConfigurations.CreateAlertDraft
  | IntelligenceModels.CaseManager.Response
  | IntelligenceModels.CaseManager.FeatureResponse
  | IntelligenceModels.CaseManager.CategoriesResponse
  | IntelligenceModels.Archive.Response
  | IntelligenceModels.Locations.PickCameraResult
  | IntelligenceModels.CameraProvisioning.AddCameraRequest
  | IntelligenceModels.CoreProvisioning.AddCoreRequest
  | IntelligenceModels.Walls.Response
  | IntelligenceModels.Walls.CreateDraft
  | IntelligenceModels.Walls.DetailRequest
  | IntelligenceModels.Charts.Response
  | IntelligenceModels.Variables.Response
  | IntelligenceModels.ZoneRegions.Response
  | IntelligenceModels.CustomObjects.ListResponse;

export interface IntelligenceResult {
  type: IntelligenceResultType;
  data: IntelligenceResultData;
}

/** One entry in the panel history — one per AI turn that updated the panel. */
export interface IntelligenceResultEntry {
  result: IntelligenceResult;
  /** Short technical label for the nav bar. */
  label: string;
  /** The user's natural language question that triggered this result. */
  prompt: string;
}

/**
 * Holds the AI tool result history so the preview panel can show past states.
 *
 * Flow:
 *  1. Before submitting, InlineChatPanelComponent calls setPendingPrompt(prompt).
 *  2. Tool handlers call the appropriate set*() method when data arrives.
 *     These capture the pending prompt and attach it to the history entry.
 *  3. The panel context bar shows the original question + navigation controls.
 *  4. Click on a user message in chat → navigateTo(index) jumps the panel.
 *
 * Overlay:
 *  Interactive in-panel actions (chart bar drill-down, findByFace, etc.) use
 *  setOverlay() instead of the regular set*() methods.  The overlay is a
 *  temporary on-top result that does NOT modify the sequential history, so
 *  clicking on any chat message always shows the correct history entry for
 *  that conversation turn.  The overlay is cleared automatically on any
 *  navigation action or when a new chat-driven tool result arrives.
 */
@Injectable({ providedIn: 'root' })
export class IntelligenceResultsService {
  private readonly _history = signal<IntelligenceResultEntry[]>([]);
  private readonly _index = signal<number>(-1);
  private readonly _pendingPrompt = signal<string>('');
  private readonly archiveSocketUpdateTimes = new Map<string, number>();

  /** Temporary result from an interactive action (not a chat turn). */
  private readonly _overlay = signal<IntelligenceResult | null>(null);
  private readonly _overlayLabel = signal<string>('');

  /** Action buttons the current result component publishes into the shared context bar. */
  private readonly _headerActions = signal<IntelligenceModels.HeaderAction[]>(
    [],
  );
  readonly headerActions = this._headerActions.asReadonly();

  /**
   * A pending "open the creation wizard" request from the `create-custom-object`
   * tool, consumed once by `custom-objects-result`.
   *
   * The wizard is the Database page's own MatDialog rather than a panel result,
   * so it cannot be a result type of its own: the tool publishes the card grid
   * as the entry behind it and asks the component to open the dialog over it.
   */
  private readonly _createCustomObjectRequest =
    signal<IntelligenceModels.CustomObjects.CreateRequest | null>(null);
  readonly createCustomObjectRequest = this._createCustomObjectRequest.asReadonly();
  /** Monotonic, so a second create call re-fires even with an unchanged prompt. */
  private createCustomObjectRequestId = 0;

  /**
   * Prompts requested programmatically by result-panel buttons (e.g. "Add clips"),
   * for the inline chat to submit to the AI. A Subject (not a signal) so the same
   * prompt can be re-fired — signals would dedupe identical values.
   */
  private readonly _promptRequest = new Subject<string>();
  readonly promptRequest$ = this._promptRequest.asObservable();

  private readonly _cameraPicked = new Subject<IntelligenceModels.Locations.PickCameraSelection>();
  public readonly cameraPicked$: Observable<IntelligenceModels.Locations.PickCameraSelection> =
    this._cameraPicked.asObservable();

  private readonly _camerasPicked = new Subject<IntelligenceModels.Locations.PickCamerasSelection>();
  public readonly camerasPicked$: Observable<IntelligenceModels.Locations.PickCamerasSelection> =
    this._camerasPicked.asObservable();

  private readonly _cameraPickCancelled = new Subject<IntelligenceModels.Locations.PickCameraCancellation>();
  public readonly cameraPickCancelled$: Observable<IntelligenceModels.Locations.PickCameraCancellation> =
    this._cameraPickCancelled.asObservable();

  private readonly _addCameraFinished = new Subject<IntelligenceModels.CameraProvisioning.AddCameraFinishedEvent>();
  public readonly addCameraFinished$: Observable<IntelligenceModels.CameraProvisioning.AddCameraFinishedEvent> =
    this._addCameraFinished.asObservable();

  private readonly _addCameraCancelled = new Subject<IntelligenceModels.CameraProvisioning.AddCameraCancelledEvent>();
  public readonly addCameraCancelled$: Observable<IntelligenceModels.CameraProvisioning.AddCameraCancelledEvent> =
    this._addCameraCancelled.asObservable();

  /** True while an interactive overlay is displayed. */
  readonly isOverlay = computed(() => this._overlay() !== null);

  readonly current = computed<IntelligenceResult | null>(() => {
    const overlay = this._overlay();
    if (overlay) return overlay;
    const h = this._history();
    const i = this._index();
    return i >= 0 && i < h.length ? h[i].result : null;
  });

  readonly currentLabel = computed<string>(() => {
    if (this._overlay()) return this._overlayLabel();
    const h = this._history();
    const i = this._index();
    return i >= 0 && i < h.length ? h[i].label : '';
  });

  readonly currentPrompt = computed<string>(() => {
    if (this._overlay()) return this._overlayLabel();
    const h = this._history();
    const i = this._index();
    return i >= 0 && i < h.length ? h[i].prompt : '';
  });

  readonly currentPosition = computed<number>(() =>
    Math.max(0, this._index() + 1),
  );
  readonly totalEntries = computed<number>(() => this._history().length);
  readonly hasMultipleEntries = computed<boolean>(
    () => this._history().length > 1,
  );
  readonly hasPrev = computed<boolean>(() => this._index() > 0);
  readonly hasNext = computed<boolean>(
    () => this._index() < this._history().length - 1,
  );

  constructor() {
    // Keep the render-only snapshot in sync with the source of truth: when a
    // person is changed through the production path, patch the active layer so
    // the tile reflects it without re-fetching (which would break the
    // positional message↔history mapping).
    const actions = inject(Actions);
    actions
      .pipe(ofType(PersonActions.trainSavedPersonSuccess), takeUntilDestroyed())
      .subscribe(({ response }) =>
        this.setTrainingStatusInCurrent(
          response.personId,
          response.trainingStatus,
        ),
      );
    actions
      .pipe(
        ofType(PersonActions.updateSavedPersonSuccess),
        takeUntilDestroyed(),
      )
      .subscribe(({ person }) =>
        this.updatePersonInCurrent(person.personId, { name: person.name }),
      );
    actions
      .pipe(
        ofType(PersonActions.bulkDeleteSavedPersonsSuccess),
        takeUntilDestroyed(),
      )
      .subscribe(({ personIds }) => this.removePersonsFromCurrent(personIds));
    actions
      .pipe(
        ofType(PersonGroupsActions.removePersonsFromGroupSuccess),
        takeUntilDestroyed(),
      )
      .subscribe(({ removedPersonIds }) => {
        // Only a members view should drop the person; a separate persons result
        // must keep them (the person still exists, just left the group).
        if (this.current()?.type === 'person-groups') {
          this.removePersonsFromCurrent(removedPersonIds);
        }
      });
    const personService = inject(PersonService);
    actions
      .pipe(
        ofType(PersonGroupsActions.addPersonsToGroupsSuccess),
        takeUntilDestroyed(),
      )
      .subscribe(({ groupIds, addedPersons }) =>
        this.addPersonsToGroupsInCurrent(
          groupIds,
          // Store persons carry raw storage paths; the members snapshot renders
          // `bestImage` as-is, so resolve to the same full bucket URL the
          // intelligence endpoint returns.
          addedPersons.map((person) => ({
            ...person,
            bestImage: personService.getPersonImagePath(
              person.bestImage,
              true,
              true,
            ),
          })),
        ),
      );
    actions
      .pipe(
        ofType(PersonGroupsActions.createPersonGroupSuccess),
        takeUntilDestroyed(),
      )
      .subscribe(({ personGroup }) =>
        this.addPersonGroupToCurrent(personGroup),
      );
    actions
      .pipe(
        ofType(PersonGroupsActions.updatePersonGroupSuccess),
        takeUntilDestroyed(),
      )
      .subscribe(({ personGroup }) =>
        this.updatePersonGroupNameInCurrent(personGroup._id, personGroup.name),
      );
    actions
      .pipe(
        ofType(PersonGroupsActions.deletePersonGroupSuccess),
        takeUntilDestroyed(),
      )
      .subscribe(({ id }) => this.removePersonGroupFromCurrent(id));
    actions
      .pipe(ofType(VehiclesActions.editVehicleSuccess), takeUntilDestroyed())
      .subscribe(({ vehicle }) =>
        this.updateVehicleInCurrent(vehicle.plate, vehicle),
      );
    actions
      .pipe(ofType(VehiclesActions.removeVehicleSuccess), takeUntilDestroyed())
      .subscribe(({ vehicle }) =>
        this.removeVehiclesFromCurrentByPlates([vehicle.plate]),
      );
    actions
      .pipe(
        ofType(VehiclesActions.removeVehiclesByPlatesSuccess),
        takeUntilDestroyed(),
      )
      .subscribe(({ plate }) =>
        this.removeVehiclesFromCurrentByPlates([plate]),
      );
    actions
      .pipe(
        ofType(VehicleGroupsActions.removeVehiclesFromGroupSuccess),
        takeUntilDestroyed(),
      )
      .subscribe(({ removedVehicleIds }) => {
        // Only a members view should drop the vehicle; a separate vehicles result
        // must keep it (the vehicle still exists, it just left the group).
        if (this.current()?.type === 'vehicle-groups') {
          this.removeVehiclesFromCurrentByIds(removedVehicleIds);
        }
      });
    actions
      .pipe(
        ofType(VehicleGroupsActions.createVehicleGroupSuccess),
        takeUntilDestroyed(),
      )
      .subscribe(({ vehicleGroup }) =>
        this.addVehicleGroupToCurrent(vehicleGroup),
      );
    actions
      .pipe(
        ofType(VehicleGroupsActions.updateVehicleGroupSuccess),
        takeUntilDestroyed(),
      )
      .subscribe(({ vehicleGroup }) =>
        this.updateVehicleGroupNameInCurrent(
          vehicleGroup._id,
          vehicleGroup.name,
        ),
      );
    actions
      .pipe(
        ofType(VehicleGroupsActions.deleteVehicleGroupSuccess),
        takeUntilDestroyed(),
      )
      .subscribe(({ id }) => this.removeVehicleGroupFromCurrent(id));
    actions
      .pipe(
        ofType(VehicleGroupsActions.addVehiclesToGroupsSuccess),
        takeUntilDestroyed(),
      )
      .subscribe(({ groupIds, addedVehicles }) =>
        this.addVehiclesToGroupsInCurrent(groupIds, addedVehicles),
      );
    actions
      .pipe(ofType(ArchiveActions.deleteArchiveSuccess), takeUntilDestroyed())
      .subscribe(({ sessionId }) => this.removeArchiveFromCurrent(sessionId));
    actions
      .pipe(ofType(ArchiveActions.addArchiveToCaseSuccess), takeUntilDestroyed())
      .subscribe(({ sessionId, caseId }) => this.updateArchiveCaseInCurrent(sessionId, caseId));
    actions
      .pipe(ofType(ArchiveActions.archiveProgressSocketUpdate), takeUntilDestroyed())
      .subscribe(({ archive, statusCode }) => {
        if (statusCode !== HttpStatusCode.NotFound) this.patchArchiveProgressInCurrent(archive);
      });
    actions
      .pipe(
        ofType(OrganizationUsersActions.updateOrganizationMemberServerCallSuccess),
        takeUntilDestroyed(),
      )
      .subscribe(({ member }) => this.updateMemberInCurrent(member));
    actions
      .pipe(ofType(OrganizationActions.removeUserSuccess), takeUntilDestroyed())
      .subscribe(({ userId }) => this.removeMemberFromCurrent(userId));
    actions
      .pipe(ofType(OrgVariablesActions.addVariablesSuccess), takeUntilDestroyed())
      .subscribe(({ variable }) => this.addVariableToCurrent(variable));
    actions
      .pipe(ofType(OrgVariablesActions.editVariablesSuccess), takeUntilDestroyed())
      .subscribe(({ variable }) => this.updateVariableInCurrent(variable));
    actions
      .pipe(ofType(OrgVariablesActions.removeVariablesSuccess), takeUntilDestroyed())
      .subscribe(({ name }) => this.removeVariableFromCurrent(name));
    actions
      .pipe(ofType(DatabaseTagsActions.createDatabaseTagSuccess), takeUntilDestroyed())
      .subscribe(({ tag }) => this.addTagToCurrent(tag));
    actions
      .pipe(ofType(DatabaseTagsActions.editDatabaseTagSuccess), takeUntilDestroyed())
      .subscribe(({ id, dto }) => this.updateTagInCurrent(id, dto));
    actions
      .pipe(ofType(DatabaseTagsActions.removeDatabaseTagSuccess), takeUntilDestroyed())
      .subscribe(({ id }) => this.removeTagFromCurrent(id));
    actions
      .pipe(ofType(ScheduleActions.deleteScheduleByIdSuccess), takeUntilDestroyed())
      .subscribe(({ id }) => this.removeTimeScheduleFromCurrent(id));
    actions
      .pipe(ofType(CaseCategoriesActions.createCategorySuccess), takeUntilDestroyed())
      .subscribe(({ category }) => this.addCaseCategoryToCurrent(category));
    actions
      .pipe(ofType(CaseCategoriesActions.updateCategorySuccess), takeUntilDestroyed())
      .subscribe(({ id, form }) => this.updateCaseCategoryInCurrent(id, form));
    actions
      .pipe(ofType(CaseCategoriesActions.deleteCategorySuccess), takeUntilDestroyed())
      .subscribe(({ id }) => this.removeCaseCategoryFromCurrent(id));
    actions
      .pipe(ofType(CustomObjectsActions.updateCustomObjectNameSuccess), takeUntilDestroyed())
      .subscribe(({ customObject }) => this.updateCustomObjectInCurrent(customObject));
    actions
      .pipe(ofType(CustomObjectStatesActions.updateCustomObjectStateNameSuccess), takeUntilDestroyed())
      .subscribe(({ stateCustomObject }) =>
        this.updateCustomObjectStateInCurrent(stateCustomObject),
      );
    actions
      .pipe(ofType(CustomObjectsActions.deleteCustomObjectSuccess), takeUntilDestroyed())
      .subscribe(({ customObjectId }) => this.removeCustomObjectFromCurrent(customObjectId));
    actions
      .pipe(ofType(CustomObjectStatesActions.deleteCustomObjectStateSuccess), takeUntilDestroyed())
      .subscribe(({ stateId }) => this.removeCustomObjectStateFromCurrent(stateId));
    actions
      .pipe(ofType(ZoneRegionsActions.setZoneCounting), takeUntilDestroyed())
      .subscribe(({ id, countingEnabled }) =>
        this.updateZoneRegionInCurrent(id, { countingEnabled }),
      );
    actions
      .pipe(
        ofType(ZoneRegionsActions.setZoneCountingSuccess),
        takeUntilDestroyed(),
      )
      .subscribe(({ zoneRegion }) =>
        this.updateZoneRegionInCurrent(zoneRegion._id, zoneRegion),
      );
    actions
      .pipe(ofType(ZoneRegionsActions.setZoneCountingFail), takeUntilDestroyed())
      .subscribe(({ id, countingEnabled }) =>
        this.updateZoneRegionInCurrent(id, {
          countingEnabled: !countingEnabled,
        }),
      );
    actions
      .pipe(
        ofType(ZoneRegionsActions.saveZoneRegionsWithCamerasSuccess),
        takeUntilDestroyed(),
      )
      .subscribe(({ response }) =>
        this.removeZoneRegionCamerasFromCurrent(
          response.deletedZoneRegionCameraIds ?? [],
        ),
      );
  }

  /** Called by InlineChatPanelComponent just before each prompt submission. */
  setPendingPrompt(prompt: string): void {
    this._pendingPrompt.set(prompt);
  }

  /** Ask the inline chat to submit a prompt to the AI (e.g. from a result-panel button). */
  requestPrompt(prompt: string): void {
    this._promptRequest.next(prompt);
  }

  /** Called by the active result component to publish its own context-bar actions. */
  setHeaderActions(actions: IntelligenceModels.HeaderAction[]): void {
    this._headerActions.set(actions);
  }

  // ── Chat-driven result setters (update sequential history) ──────────────────

  setOrganizationMembers(data: IntelligenceModels.Organizations.MembersResponse, label: string): void {
    this.pushEntry({ type: 'organization-members', data }, label);
  }

  setDeviceHealth(
    data: IntelligenceModels.DeviceHealth.Response,
    label: string,
  ): void {
    this.pushEntry({ type: 'device-health', data }, label);
  }

  setDeviceList(
    data: IntelligenceModels.DeviceList.Response,
    label: string,
  ): void {
    this.pushEntry({ type: 'device-list', data }, label);
  }

  setLocations(
    data: IntelligenceModels.Locations.Response,
    label: string,
  ): void {
    this.pushEntry({ type: 'locations', data }, label);
  }

  setPersons(data: IntelligenceModels.Persons.Response, label: string): void {
    this.pushEntry({ type: 'persons', data }, label);
  }

  setPersonGroups(
    data:
      | IntelligenceModels.PersonGroups.Response
      | IntelligenceModels.PersonGroups.MembersResponse,
    label: string,
  ): void {
    this.pushEntry({ type: 'person-groups', data }, label);
  }

  setDashboards(
    data: IntelligenceModels.Dashboards.Response,
    label: string,
  ): void {
    this.pushEntry({ type: 'dashboards', data }, label);
  }

  setVehicles(data: IntelligenceModels.Vehicles.Response, label: string): void {
    this.pushEntry({ type: 'vehicles', data }, label);
  }

  setVehicleGroups(
    data:
      | IntelligenceModels.VehicleGroups.Response
      | IntelligenceModels.VehicleGroups.MembersResponse,
    label: string,
  ): void {
    this.pushEntry({ type: 'vehicle-groups', data }, label);
  }

  setSearch(data: IntelligenceModels.Search.Response, label: string): void {
    this.pushEntry({ type: 'search', data }, label);
  }

  setAlerts(data: IntelligenceModels.Alerts.Response, label: string): void {
    this.pushEntry({ type: 'alerts', data }, label);
  }

  setAlertConfigurations(
    data: IntelligenceModels.AlertConfigurations.Response,
    label: string,
  ): void {
    this.pushEntry({ type: 'alert-configurations', data }, label);
  }

  setAlertTemplatesDraft(
    data: IntelligenceModels.AlertConfigurations.CreateAlertDraft,
    label: string,
  ): void {
    this.pushEntry({ type: 'alert-templates', data }, label);
  }

  setCaseManager(
    data: IntelligenceModels.CaseManager.Response,
    label: string,
  ): void {
    this.pushEntry({ type: 'case-manager', data }, label);
  }

  setCaseManagerCategories(
    data: IntelligenceModels.CaseManager.CategoriesResponse,
    label: string,
  ): void {
    this.pushEntry({ type: 'case-manager-category', data }, label);
  }

  setArchive(data: IntelligenceModels.Archive.Response, label: string): void {
    this.pushEntry({ type: 'archive', data }, label);
  }

  setVariables(data: IntelligenceModels.Variables.Response, label: string): void {
    this.pushEntry({ type: 'variables', data }, label);
  }

  setZoneRegions(data: IntelligenceModels.ZoneRegions.Response, label: string): void {
    this.pushEntry({ type: 'zone-regions', data }, label);
  }

  setUserOrganizations(
    data: IntelligenceModels.Organizations.UserOrganizationsViewResponse,
    label: string,
  ): void {
    this.pushEntry({ type: 'user-organizations', data }, label);
  }

  setTags(data: IntelligenceModels.Tags.ListResponse, label: string): void {
    this.pushEntry({ type: 'tags', data }, label);
  }

  setEventTags(data: IntelligenceModels.EventTags.ListResponse, label: string): void {
    this.pushEntry({ type: 'event-tags', data }, label);
  }

  setTimeSchedules(data: IntelligenceModels.TimeSchedules.ListResponse, label: string): void {
    this.pushEntry({ type: 'time-schedule', data }, label);
  }

  /**
   * Show the schedule editor over whatever the panel is on, as an overlay: it
   * adds no history entry, so the context bar's `‹` crumb comes back to the
   * listing unchanged.
   */
  setCreateTimeSchedule(
    data: IntelligenceModels.TimeSchedules.CreateDraft,
    label = 'Create time schedule',
  ): void {
    this.setOverlay(
      data,
      IntelligenceModels.TimeSchedules.PanelResultType.CreateTimeSchedule,
      label,
    );
  }

  /**
   * Swap the entry behind the create-time-schedule overlay for a freshly fetched
   * listing and close the overlay.
   *
   * Order matters exactly as in `replaceCurrentWithEventTags`: `hadOverlay` is
   * read before the history write and the overlay is cleared after it, otherwise
   * `current()` briefly resolves to the stale entry and the panel flashes the
   * pre-save list.
   */
  replaceCurrentWithTimeSchedules(
    response: IntelligenceModels.TimeSchedules.ListResponse,
    label = `${response.count} time schedules`,
  ): void {
    const nextResult: IntelligenceResult = { type: 'time-schedule', data: response };
    const hadOverlay = this._overlay() !== null;

    this._history.update((history) => {
      const index = this._index();
      const entry = history[index];
      if (!entry) return history;

      const next = [...history];
      next[index] = { ...entry, label, result: nextResult };
      return next;
    });

    if (hadOverlay) this.clearOverlay();
  }

  setCustomObjects(
    data: IntelligenceModels.CustomObjects.ListResponse,
    label: string,
  ): void {
    this.pushEntry({ type: 'custom-objects', data }, label);
  }

  /**
   * Show the card grid and ask it to open the creation wizard over itself.
   *
   * The listing is pushed first so the panel behind the dialog is the real grid,
   * and so cancelling the wizard leaves the user somewhere useful.
   */
  requestCreateCustomObject(
    data: IntelligenceModels.CustomObjects.ListResponse,
    prompt: string | undefined,
    label: string,
  ): void {
    this.setCustomObjects(data, label);
    this.createCustomObjectRequestId += 1;
    this._createCustomObjectRequest.set({
      id: this.createCustomObjectRequestId,
      ...(prompt ? { prompt } : {}),
    });
  }

  /** Take the pending wizard request; the panel opens the dialog at most once per call. */
  consumeCreateCustomObjectRequest(): IntelligenceModels.CustomObjects.CreateRequest | null {
    const request = this._createCustomObjectRequest();
    if (request) this._createCustomObjectRequest.set(null);
    return request;
  }

  /**
   * Show the create-event-tag editor as an overlay over the list that opened it,
   * so the `‹` crumb returns to that list without touching history.
   */
  setCreateEventTag(
    data: IntelligenceModels.EventTags.CreateDraft,
    label = 'Create event tag',
  ): void {
    this.setOverlay(
      data,
      IntelligenceModels.EventTags.PanelResultType.CreateEventTag,
      label,
    );
  }

  /**
   * Swap the entry behind the create-event-tag overlay for a freshly fetched
   * listing and close the overlay.
   *
   * Order matters: `hadOverlay` is read before the history write and the overlay
   * is cleared after it, otherwise `current()` briefly resolves to the stale
   * entry and the panel flashes the pre-save list.
   */
  replaceCurrentWithEventTags(
    response: IntelligenceModels.EventTags.ListResponse,
    label = `${response.count} event tags`,
  ): void {
    const nextResult: IntelligenceResult = { type: 'event-tags', data: response };
    const hadOverlay = this._overlay() !== null;

    this._history.update((history) => {
      const index = this._index();
      const entry = history[index];
      if (!entry) return history;

      const next = [...history];
      next[index] = { ...entry, label, result: nextResult };
      return next;
    });

    if (hadOverlay) this.clearOverlay();
  }

  /**
   * Drop one deleted event tag from the panel's current listing, and with it one
   * from the count the quota line reads.
   *
   * The snapshot is patched rather than re-fetched: the delete already succeeded,
   * so the row must leave immediately, and a failed re-fetch must not be able to
   * put it back on screen.
   */
  removeEventTagFromCurrent(eventTagId: string): void {
    this.patchActiveData((data) => {
      if (this.current()?.type !== 'event-tags') return data;
      const response = data as IntelligenceModels.EventTags.ListResponse;
      const eventTags = response.eventTags.filter((eventTag) => eventTag._id !== eventTagId);
      if (eventTags.length === response.eventTags.length) return response;

      return { ...response, eventTags, count: Math.max(0, response.count - 1) };
    });
  }

  replaceCurrentWithZoneRegions(
    response: IntelligenceModels.ZoneRegions.ListResponse,
    label = `${response.count} zone regions`,
  ): void {
    const nextResult: IntelligenceResult = { type: 'zone-regions', data: response };
    const hadOverlay = this._overlay() !== null;

    this._history.update((history) => {
      const index = this._index();
      const entry = history[index];
      if (!entry) return history;

      const next = [...history];
      next[index] = { ...entry, label, result: nextResult };
      return next;
    });

    if (hadOverlay) this.clearOverlay();
  }

  replaceZoneRegionsInCurrent(
    response: IntelligenceModels.ZoneRegions.ListResponse,
  ): void {
    this.patchActiveData((data) => {
      if (this.current()?.type !== 'zone-regions') return data;
      const current = data as IntelligenceModels.ZoneRegions.Response;
      return current.view === 'list' ? response : current;
    });
  }

  private updateZoneRegionInCurrent(
    id: string,
    changes: Partial<ZoneRegionsModels.ZoneRegionDocument>,
  ): void {
    this.patchActiveData((data) => {
      if (this.current()?.type !== 'zone-regions') return data;
      const response = data as IntelligenceModels.ZoneRegions.Response;

      if (response.view === 'list') {
        if (!response.zoneRegions.some((zoneRegion) => zoneRegion._id === id)) {
          return response;
        }
        return {
          ...response,
          zoneRegions: response.zoneRegions.map((zoneRegion) =>
            zoneRegion._id === id ? { ...zoneRegion, ...changes } : zoneRegion,
          ),
        };
      }

      return response.zoneRegion._id === id
        ? {
            ...response,
            zoneRegion: { ...response.zoneRegion, ...changes },
          }
        : response;
    });
  }

  private removeZoneRegionCamerasFromCurrent(cameraIds: string[]): void {
    if (!cameraIds.length) return;
    const deletedIds = new Set(cameraIds);

    this.patchActiveData((data) => {
      if (this.current()?.type !== 'zone-regions') return data;
      const response = data as IntelligenceModels.ZoneRegions.Response;
      if (response.view !== 'detail') return response;

      return {
        ...response,
        cameras: response.cameras.filter((camera) => !deletedIds.has(camera._id)),
        cameraNames: Object.fromEntries(
          Object.entries(response.cameraNames).filter(
            ([cameraId]) => !deletedIds.has(cameraId),
          ),
        ),
      };
    });
  }

  /**
   * Prepend a tag created through the store to the active tags snapshot, so the
   * Cmd+K panel shows it without re-running `list-tags`. De-duped by id: a tag
   * already in the snapshot is replaced in place and the count stays put.
   *
   * The `tags` result rows also carry the intelligence endpoint's `orgId`, which
   * the store's document type does not declare even though the create response
   * returns it — hence the widening rather than a re-fetch.
   */
  private addTagToCurrent(tag: DatabaseTagsModel.DatabaseTagDocument): void {
    this.patchActiveData((data) => {
      if (this.current()?.type !== 'tags') return data;
      const tagsData = data as IntelligenceModels.Tags.ListResponse;
      const created = tag as IntelligenceModels.Tags.Tag;
      const others = tagsData.tags.filter((item) => item._id !== created._id);
      const isNew = others.length === tagsData.tags.length;
      return {
        ...tagsData,
        tags: [created, ...others],
        count: isNew ? tagsData.count + 1 : tagsData.count,
      };
    });
  }

  /**
   * Merge an edited tag into the active tags snapshot. The edit effect reports
   * the submitted DTO rather than a re-read document, so the patch is merged
   * over the row already held; row order and the count are untouched, because
   * an edit neither adds nor removes a tag.
   */
  private updateTagInCurrent(
    id: string,
    dto: Partial<DatabaseTagsModel.DatabaseTagCreateDto>,
  ): void {
    this.patchActiveData((data) => {
      if (this.current()?.type !== 'tags') return data;
      const tagsData = data as IntelligenceModels.Tags.ListResponse;
      return {
        ...tagsData,
        tags: tagsData.tags.map((tag) => (tag._id === id ? { ...tag, ...dto } : tag)),
      };
    });
  }

  /** Drop a deleted tag from the active tags snapshot, decrementing the count with it. */
  private removeTagFromCurrent(id: string): void {
    this.patchActiveData((data) => {
      if (this.current()?.type !== 'tags') return data;
      const tagsData = data as IntelligenceModels.Tags.ListResponse;
      const tags = tagsData.tags.filter((tag) => tag._id !== id);
      if (tags.length === tagsData.tags.length) return tagsData;
      return { ...tagsData, tags, count: Math.max(0, tagsData.count - 1) };
    });
  }

  /**
   * Drop a deleted schedule from the panel's snapshot, mirroring
   * `removeTagFromCurrent`: the delete goes through the store so the Database
   * page stays in step, and the Cmd+K listing settles from the success action
   * rather than re-running `list-time-schedules`.
   */
  private removeTimeScheduleFromCurrent(id: string): void {
    this.patchActiveData((data) => {
      if (this.current()?.type !== 'time-schedule') return data;
      const scheduleData = data as IntelligenceModels.TimeSchedules.ListResponse;
      const schedules = scheduleData.schedules.filter((schedule) => schedule._id !== id);
      if (schedules.length === scheduleData.schedules.length) return scheduleData;
      return { ...scheduleData, schedules, count: Math.max(0, scheduleData.count - 1) };
    });
  }

  /**
   * Show a category created from the Cmd+K panel — or from the Database page while
   * the panel is open — without re-running `list-case-categories`. The panel
   * renders `retentionPeriod` through `RetentionPeriodsMap` itself, so the raw
   * numeric document goes in unchanged.
   */
  private addCaseCategoryToCurrent(category: CaseCategoriesModel.CaseCategoryDocument): void {
    this.patchActiveData((data) => {
      if (this.current()?.type !== 'case-manager-category') return data;
      const categoryData = data as IntelligenceModels.CaseManager.CategoriesResponse;
      const others = categoryData.categories.filter((item) => item._id !== category._id);
      // A re-created id replaces its row instead of doubling it, and only a
      // genuinely new one bumps the count.
      const isNew = others.length === categoryData.categories.length;
      return {
        ...categoryData,
        categories: [category, ...others],
        count: isNew ? categoryData.count + 1 : categoryData.count,
      };
    });
  }

  /**
   * Merge an edited category's submitted fields into the snapshot. `updateCategory`
   * answers with the form rather than the document, which is enough: only `name`
   * and `retentionPeriod` are editable.
   */
  private updateCaseCategoryInCurrent(
    id: string,
    form: CaseCategoriesModel.CreateCategoryForm,
  ): void {
    this.patchActiveData((data) => {
      if (this.current()?.type !== 'case-manager-category') return data;
      const categoryData = data as IntelligenceModels.CaseManager.CategoriesResponse;
      return {
        ...categoryData,
        categories: categoryData.categories.map((category) =>
          category._id === id ? { ...category, ...form } : category,
        ),
      };
    });
  }

  /** Drop a deleted category from the active snapshot, decrementing the count with it. */
  private removeCaseCategoryFromCurrent(id: string): void {
    this.patchActiveData((data) => {
      if (this.current()?.type !== 'case-manager-category') return data;
      const categoryData = data as IntelligenceModels.CaseManager.CategoriesResponse;
      const categories = categoryData.categories.filter((category) => category._id !== id);
      if (categories.length === categoryData.categories.length) return categoryData;
      return { ...categoryData, categories, count: Math.max(0, categoryData.count - 1) };
    });
  }

  /**
   * Swap the active entry for a freshly fetched custom-objects listing, without
   * adding a chat-history entry. Mirrors `replaceCurrentWithZoneRegions`.
   *
   * Used after a create: the training dialog's classifier flow closes with a
   * name only, so there is no entity to splice into the snapshot and the panel
   * re-fetches instead. The label is re-derived because the count changed.
   */
  replaceCurrentWithCustomObjects(
    response: IntelligenceModels.CustomObjects.ListResponse,
    label = `${response.count} custom objects`,
  ): void {
    const nextResult: IntelligenceResult = { type: 'custom-objects', data: response };
    const hadOverlay = this._overlay() !== null;

    this._history.update((history) => {
      const index = this._index();
      const entry = history[index];
      if (!entry) return history;

      const next = [...history];
      next[index] = { ...entry, label, result: nextResult };
      return next;
    });

    if (hadOverlay) this.clearOverlay();
  }

  private updateCustomObjectInCurrent(
    customObject: IntelligenceModels.CustomObjects.CustomObject,
  ): void {
    if (customObject.customObjectId === undefined) return;

    this.patchActiveData((data) => {
      if (this.current()?.type !== 'custom-objects') return data;
      const response = data as IntelligenceModels.CustomObjects.ListResponse;
      if (
        !response.customObjects.some(
          (item) => item.customObjectId === customObject.customObjectId,
        )
      ) {
        return response;
      }
      return {
        ...response,
        customObjects: response.customObjects.map((item) =>
          item.customObjectId === customObject.customObjectId
            ? { ...item, ...customObject }
            : item,
        ),
      };
    });
  }

  private updateCustomObjectStateInCurrent(
    customObjectState: IntelligenceModels.CustomObjects.CustomObjectState,
  ): void {
    this.patchActiveData((data) => {
      if (this.current()?.type !== 'custom-objects') return data;
      const response = data as IntelligenceModels.CustomObjects.ListResponse;
      if (!response.customObjectStates.some((item) => item.id === customObjectState.id)) {
        return response;
      }
      return {
        ...response,
        customObjectStates: response.customObjectStates.map((item) =>
          item.id === customObjectState.id ? { ...item, ...customObjectState } : item,
        ),
      };
    });
  }

  private removeCustomObjectFromCurrent(customObjectId: number): void {
    this.patchActiveData((data) => {
      if (this.current()?.type !== 'custom-objects') return data;
      const response = data as IntelligenceModels.CustomObjects.ListResponse;
      const customObjects = response.customObjects.filter(
        (item) => item.customObjectId !== customObjectId,
      );
      if (customObjects.length === response.customObjects.length) return response;
      return { ...response, customObjects, count: Math.max(0, response.count - 1) };
    });
  }

  private removeCustomObjectStateFromCurrent(stateId: number): void {
    this.patchActiveData((data) => {
      if (this.current()?.type !== 'custom-objects') return data;
      const response = data as IntelligenceModels.CustomObjects.ListResponse;
      const customObjectStates = response.customObjectStates.filter((item) => item.id !== stateId);
      if (customObjectStates.length === response.customObjectStates.length) return response;
      return { ...response, customObjectStates, count: Math.max(0, response.count - 1) };
    });
  }

  /** Append a created variable only when the active result is the variables list. */
  private addVariableToCurrent(variable: OrgVariablesModel.Variable): void {
    this.patchActiveData((data) => {
      if (this.current()?.type !== 'variables') return data;
      const variablesData = data as IntelligenceModels.Variables.Response;
      return {
        ...variablesData,
        variables: [...variablesData.variables, variable],
        count: variablesData.count + 1,
      };
    });
  }

  private updateVariableInCurrent(variable: OrgVariablesModel.Variable): void {
    this.patchActiveData((data) => {
      if (this.current()?.type !== 'variables') return data;
      const variablesData = data as IntelligenceModels.Variables.Response;
      return {
        ...variablesData,
        variables: variablesData.variables.map((item) => (item.name === variable.name ? variable : item)),
      };
    });
  }

  private removeVariableFromCurrent(name: string): void {
    this.patchActiveData((data) => {
      if (this.current()?.type !== 'variables') return data;
      const variablesData = data as IntelligenceModels.Variables.Response;
      const variables = variablesData.variables.filter((variable) => variable.name !== name);
      if (variables.length === variablesData.variables.length) return variablesData;
      return { ...variablesData, variables, count: Math.max(0, variablesData.count - 1) };
    });
  }

  /**
   * Show the camera selector as an overlay, not a history entry: it is a
   * transient prompt for one answer, so once the pick is made it must vanish
   * (a still-interactive selector reads as "you can change this", but the model
   * has already continued with the chosen camera).
   */
  public setPickCamera(data: IntelligenceModels.Locations.PickCameraToolParams, label: string): void {
    const result: IntelligenceModels.Locations.PickCameraResult = {
      ...data,
      panelResultType: IntelligenceModels.Locations.PanelResultType.PickCamera,
    };
    this.setOverlay(result, IntelligenceModels.Locations.PanelResultType.PickCamera, label);
  }

  public resolveCameraPick(selection: IntelligenceModels.Locations.PickCameraSelection): void {
    this.clearOverlay();
    this._cameraPicked.next(selection);
  }

  public resolveCamerasPick(selection: IntelligenceModels.Locations.PickCamerasSelection): void {
    this.clearOverlay();
    this._camerasPicked.next(selection);
  }

  /**
   * Abandon a pending camera pick so the awaiting tool handler can resolve.
   * Called from every path that tears the selector down without an answer:
   * leaving AI chat mode, starting a new chat, destroying the chat panel, and
   * closing the palette. Emitting with no pending pick is a harmless no-op.
   */
  public cancelCameraPick(): void {
    if (
      this.current()?.type ===
      IntelligenceModels.Locations.PanelResultType.PickCamera
    ) {
      this.clearOverlay();
    }
    this._cameraPickCancelled.next({
      cancelled: true,
      reason: IntelligenceModels.Locations.PickCameraCancelReason.Dismissed,
    });
  }

  /**
   * Show the add-camera wizard as a transient overlay. Its request ID keeps a
   * teardown event scoped to the single tool invocation that owns this wizard.
   */
  public setAddCamera(
    request: IntelligenceModels.CameraProvisioning.AddCameraRequest,
    label: string,
  ): void {
    this.setOverlay(
      request,
      IntelligenceModels.CameraProvisioning.PanelResultType.AddCamera,
      label,
    );
  }

  /** Show the existing Add Core form inline in the Cmd+K result surface. */
  public setAddCore(
    request: IntelligenceModels.CoreProvisioning.AddCoreRequest,
    label: string,
  ): void {
    this.setOverlay(
      request,
      IntelligenceModels.CoreProvisioning.PanelResultType.AddCore,
      label,
    );
  }

  public resolveAddCamera(requestId: string, locationName: string): void {
    if (!this.isActiveAddCameraRequest(requestId)) return;
    this.clearOverlay();
    this._addCameraFinished.next({
      requestId,
      outcome: { created: true, locationName },
    });
  }

  /** Resolve only the active add-camera invocation when its UI is torn down. */
  public cancelAddCamera(requestId?: string): void {
    const activeRequest = this.activeAddCameraRequest();
    if (!activeRequest || (requestId && activeRequest.requestId !== requestId)) return;
    this.clearOverlay();
    this._addCameraCancelled.next({
      requestId: activeRequest.requestId,
      outcome: {
        cancelled: true,
        reason: IntelligenceModels.CameraProvisioning.CancelReason.Dismissed,
      },
    });
  }

  setWalls(data: IntelligenceModels.Walls.Response, label: string): void {
    this.pushEntry({ type: 'wall', data }, label);
  }

  setWallDetail(
    data: IntelligenceModels.Walls.DetailRequest,
    label: string,
  ): void {
    this.pushEntry({ type: 'wall-camera', data }, label);
  }

  setCreateWallDraft(
    data: IntelligenceModels.Walls.CreateDraft,
    label: string,
  ): void {
    this.pushEntry({ type: 'create-wall', data }, label);
  }

  setChart(data: IntelligenceModels.Charts.Response, label: string): void {
    this.pushEntry({ type: 'chart', data }, label);
  }

  /**
   * Append a fetched page of archives to the current archive result entry,
   * advancing the `latestTs` cursor and the `hasMore` flag. Used by the
   * archive-result component's infinite scroll. De-dupes by sessionId.
   */
  appendArchivesToCurrent(
    items: Archive.ArchiveDocument[],
    nextLatestTs: number | null,
    hasMore: boolean,
  ): void {
    this.patchActiveData((data) => {
      if (!('archives' in data)) return data;
      const seen = new Set(data.archives.map((archive) => archive.sessionId));
      const fresh = items.filter((archive) => !seen.has(archive.sessionId));
      const archives = [...data.archives, ...fresh];
      return {
        ...data,
        archives,
        count: archives.length,
        filter: { ...data.filter, latestTs: nextLatestTs },
        hasMore,
      };
    });
  }

  /**
   * Append a fetched page of walls to the current wall result entry, advancing
   * the `latestTs` cursor and the `hasMore` flag. Used by the wall-result
   * component's infinite scroll. De-dupes by `_id`.
   */
  appendWallsToCurrent(
    items: IntelligenceModels.Walls.Item[],
    nextLatestTs: number | null,
    hasMore: boolean,
  ): void {
    this.patchActiveData((data) => {
      if (!('walls' in data)) return data;
      const seen = new Set(data.walls.map((wall) => wall._id));
      const fresh = items.filter((wall) => !seen.has(wall._id));
      const walls = [...data.walls, ...fresh];
      return {
        ...data,
        walls,
        count: walls.length,
        filter: { ...data.filter, latestTs: nextLatestTs },
        hasMore,
      };
    });
  }

  /**
   * Append an alert page to the active alert result while preserving its initial
   * aggregate response data. Alert cursors can overlap at their timestamp
   * boundary, so the page is de-duped by the alert's stable identity.
   */
  appendAlertsToCurrent(
    items: IntelligenceModels.Alerts.Item[],
    nextLatestTs: number | null,
    hasMore: boolean,
  ): void {
    this.patchActiveData((data) => {
      if (this.current()?.type !== 'alerts') return data;

      const alertsData = data as IntelligenceModels.Alerts.Response;
      const seen = new Set(
        alertsData.results.map((item) =>
          item.alertId ?? item.monitoringAlert?._id,
        ),
      );
      const fresh = items.filter((item) => {
        const id = item.alertId ?? item.monitoringAlert?._id;
        if (seen.has(id)) return false;
        seen.add(id);
        return true;
      });

      return {
        ...alertsData,
        results: [...alertsData.results, ...fresh],
        filter: {
          ...alertsData.filter,
          latestTs: nextLatestTs ?? alertsData.filter?.latestTs,
        },
        hasMore: fresh.length ? hasMore : false,
      };
    });
  }

  /** Prepend newly received socket alerts without advancing the pagination cursor. */
  prependAlertsToCurrent(items: IntelligenceModels.Alerts.Item[]): number {
    let prepended = 0;
    this.patchActiveData((data) => {
      if (this.current()?.type !== 'alerts') return data;

      const alertsData = data as IntelligenceModels.Alerts.Response;
      const seen = new Set(
        alertsData.results.map((item) =>
          item.alertId ?? item.monitoringAlert?._id,
        ),
      );
      const fresh = items.filter((item) => {
        const id = item.alertId ?? item.monitoringAlert?._id;
        if (seen.has(id)) return false;
        seen.add(id);
        return true;
      });
      prepended = fresh.length;
      if (!fresh.length) return alertsData;

      return {
        ...alertsData,
        results: [...fresh, ...alertsData.results],
        totalCount: (alertsData.totalCount ?? 0) + fresh.length,
      };
    });
    return prepended;
  }

  /** Replace the active alert result after an in-place filter re-query. */
  replaceAlertsInCurrent(response: IntelligenceModels.Alerts.Response): void {
    this.patchActiveData((data) => {
      if (this.current()?.type !== 'alerts') return data;
      const alertsData = data as IntelligenceModels.Alerts.Response;
      return {
        ...alertsData,
        results: response.results,
        totalCount: response.totalCount,
        filter: response.filter,
        hasMore: response.hasMore ?? false,
        breakdown: response.breakdown,
        topN: response.topN,
        uiState: response.uiState ?? alertsData.uiState,
      };
    });
  }

  /**
   * Replace the current wall result entry's data in place after a filter
   * re-query — resets the list, count, cursor, and `hasMore` WITHOUT pushing a
   * new history entry (so typing in a filter doesn't create back-crumbs). Used
   * by the walls-result component's filter bar.
   */
  replaceWallsInCurrent(response: IntelligenceModels.Walls.Response): void {
    this.patchActiveData((data) => {
      if (!('walls' in data)) return data;
      return {
        ...data,
        walls: response.walls,
        count: response.count,
        filter: response.filter,
        hasMore: response.hasMore ?? false,
      };
    });
  }

  /** Replace the active create-wall draft with a refreshed wall list. */
  replaceCurrentWithWalls(
    response: IntelligenceModels.Walls.Response,
    label = `${response.count} walls`,
  ): void {
    const nextResult: IntelligenceResult = {
      type: 'wall',
      data: {
        ...response,
        hasMore: response.hasMore ?? false,
      },
    };

    const hadOverlay = this._overlay() !== null;

    this._history.update((history) => {
      const index = this._index();
      const entry = history[index];
      if (!entry) {
        return history;
      }

      const next = [...history];
      next[index] = {
        ...entry,
        label,
        result: nextResult,
      };
      return next;
    });

    if (hadOverlay) {
      this.clearOverlay();
    }
  }

  /** Remove a wall from the current wall result entry (after a confirmed delete). */
  removeWallFromCurrent(wallId: string): void {
    this._history.update((h) => {
      const i = this._index();
      const entry = h[i];
      if (!entry || entry.result.type !== 'wall') return h;
      const data = entry.result.data as IntelligenceModels.Walls.Response;
      const walls = data.walls.filter((wall) => wall._id !== wallId);
      if (walls.length === data.walls.length) return h;
      const next = [...h];
      next[i] = {
        ...entry,
        result: {
          ...entry.result,
          data: { ...data, walls, count: Math.max(0, data.count - 1) },
        },
      };
      return next;
    });
  }

  /**
   * Replace the current alert-configurations entry's data in place after a filter
   * re-query — resets the list, count, filter, and `hasMore` WITHOUT pushing a new
   * history entry. Mirrors `replaceWallsInCurrent`.
   */
  replaceAlertConfigurationsInCurrent(
    response: IntelligenceModels.AlertConfigurations.Response,
  ): void {
    this.patchActiveData((data) => {
      // `filter` also discriminates against device-health, which has `items` too.
      if (!('items' in data) || !('filter' in data)) return data;
      return {
        ...data,
        items: response.items,
        count: response.count,
        filter: response.filter,
        hasMore: response.hasMore ?? false,
        uiState: response.uiState ?? data.uiState,
      };
    });
  }

  /** Replace the active case-manager result after an in-place filter re-query. */
  replaceCaseManagerInCurrent(
    response: IntelligenceModels.CaseManager.Response,
  ): void {
    this.patchActiveData((data) => {
      if (this.current()?.type !== 'case-manager') return data;
      const caseManagerData = data as IntelligenceModels.CaseManager.Response;
      return {
        ...caseManagerData,
        cases: response.cases,
        count: response.count,
        filter: response.filter,
        message: response.message,
      };
    });
  }

  /** Replace the active create/edit overlay or history result with a refreshed configurations list. */
  replaceCurrentWithAlertConfigurations(
    response: IntelligenceModels.AlertConfigurations.Response,
    label = `${response.count} alert configurations`,
  ): void {
    const nextResult: IntelligenceResult = {
      type: 'alert-configurations',
      data: {
        ...response,
        hasMore: response.hasMore ?? false,
      },
    };
    const hadOverlay = this._overlay() !== null;

    this._history.update((history) => {
      const index = this._index();
      const entry = history[index];
      if (!entry) return history;

      const next = [...history];
      next[index] = {
        ...entry,
        label,
        result: nextResult,
      };
      return next;
    });

    if (hadOverlay) {
      this.clearOverlay();
    }
  }

  /** Remove an alert configuration from the current entry (after a confirmed delete). */
  removeAlertConfigurationFromCurrent(eventId: string): void {
    this._history.update((h) => {
      const i = this._index();
      const entry = h[i];
      if (!entry || entry.result.type !== 'alert-configurations') return h;
      const data = entry.result
        .data as IntelligenceModels.AlertConfigurations.Response;
      const items = data.items.filter((item) => item._id !== eventId);
      if (items.length === data.items.length) return h;
      const next = [...h];
      next[i] = {
        ...entry,
        result: {
          ...entry.result,
          data: { ...data, items, count: Math.max(0, data.count - 1) },
        },
      };
      return next;
    });
  }

  /** Merge `changes` into a single alert configuration (e.g. on enable/disable success). */
  updateAlertConfigurationInCurrent(
    eventId: string,
    changes: Partial<IntelligenceModels.AlertConfigurations.Item>,
  ): void {
    this.patchActiveData((data) => {
      if (!('items' in data)) return data;
      return {
        ...data,
        items: data.items.map((item) =>
          item._id === eventId ? { ...item, ...changes } : item,
        ),
      };
    });
  }

  /** Prepend a newly created case to the current case-manager result entry. */
  prependCaseToCurrent(newCase: IntelligenceModels.CaseManager.Case): void {
    this._history.update((h) => {
      const i = this._index();
      const entry = h[i];
      if (!entry || entry.result.type !== 'case-manager') return h;
      const data = entry.result.data as IntelligenceModels.CaseManager.Response;
      const next = [...h];
      next[i] = {
        ...entry,
        result: {
          ...entry.result,
          data: {
            ...data,
            cases: [newCase, ...data.cases],
            count: data.count + 1,
          },
        },
      };
      return next;
    });
  }

  /** Remove a case from the current case-manager result entry (after a confirmed delete). */
  removeCaseFromCurrent(caseId: string): void {
    this._history.update((h) => {
      const i = this._index();
      const entry = h[i];
      if (!entry || entry.result.type !== 'case-manager') return h;
      const data = entry.result.data as IntelligenceModels.CaseManager.Response;
      const cases = data.cases.filter((caseItem) => caseItem._id !== caseId);
      if (cases.length === data.cases.length) return h;
      const next = [...h];
      next[i] = {
        ...entry,
        result: {
          ...entry.result,
          data: { ...data, cases, count: Math.max(0, data.count - 1) },
        },
      };
      return next;
    });
  }

  /** Replace a case in the current case-manager result entry (after a confirmed update). */
  updateCaseInCurrent(updatedCase: IntelligenceModels.CaseManager.Case): void {
    this._history.update((h) => {
      const i = this._index();
      const entry = h[i];
      if (!entry || entry.result.type !== 'case-manager') return h;
      const data = entry.result.data as IntelligenceModels.CaseManager.Response;
      if (!data.cases.some((caseItem) => caseItem._id === updatedCase._id)) return h;
      const next = [...h];
      next[i] = {
        ...entry,
        result: {
          ...entry.result,
          data: {
            ...data,
            cases: data.cases.map((caseItem) =>
              caseItem._id === updatedCase._id ? { ...caseItem, ...updatedCase } : caseItem,
            ),
          },
        },
      };
      return next;
    });
  }

  /** Remove an organization member from the current members entry (after a confirmed remove). */
  removeMemberFromCurrent(memberId: string): void {
    this._history.update((h) => {
      const i = this._index();
      const entry = h[i];
      if (!entry || entry.result.type !== 'organization-members') return h;
      const data = entry.result.data as IntelligenceModels.Organizations.MembersResponse;
      const members = data.members.filter((member) => member.id !== memberId);
      if (members.length === data.members.length) return h;
      const next = [...h];
      next[i] = {
        ...entry,
        result: {
          ...entry.result,
          data: { ...data, members, count: Math.max(0, data.count - 1) },
        },
      };
      return next;
    });
  }

  /** Merge a store member update (edit modal save) into the current members entry. */
  updateMemberInCurrent(member: OrganizationModels.OrganizationMember): void {
    this._history.update((h) => {
      const i = this._index();
      const entry = h[i];
      if (!entry || entry.result.type !== 'organization-members') return h;
      const data = entry.result.data as IntelligenceModels.Organizations.MembersResponse;
      if (!data.members.some((row) => row.id === member._id)) return h;
      const next = [...h];
      next[i] = {
        ...entry,
        result: {
          ...entry.result,
          data: {
            ...data,
            members: data.members.map((row) =>
              row.id === member._id
                ? {
                    ...row,
                    firstname: member.firstname ?? row.firstname,
                    lastname: member.lastname ?? row.lastname,
                    email: member.email ?? row.email,
                    groupIds: member.groupIds ?? row.groupIds,
                    role: member.roleBehavior ?? row.role,
                  }
                : row,
            ),
          },
        },
      };
      return next;
    });
  }

  // ── Snapshot mutators ───────────────────────────────────────────────────────
  //
  // These patch the active result layer in place after a CONFIRMED production
  // mutation — the overlay when a group's members are shown, otherwise the
  // current history entry. They never re-push (which would break the positional
  // message↔history mapping) and never re-fetch.

  /** Patch a single person's training status (on train success). */
  setTrainingStatusInCurrent(personId: number, trainingStatus: number): void {
    this.mapCurrentPersons((persons) =>
      persons.map((p) =>
        p.personId === personId ? { ...p, trainingStatus } : p,
      ),
    );
  }

  /** Merge `changes` into a single person (on update success). */
  updatePersonInCurrent(
    personId: number,
    changes: Partial<IntelligenceModels.Persons.Person>,
  ): void {
    this.mapCurrentPersons((persons) =>
      persons.map((p) => (p.personId === personId ? { ...p, ...changes } : p)),
    );
  }

  /** Drop persons and adjust counts (on delete / remove-from-group success). */
  removePersonsFromCurrent(personIds: number[]): void {
    const ids = new Set(personIds);
    this.patchActiveData((data) => {
      if (!('persons' in data)) return data;
      const persons = data.persons.filter((p) => !ids.has(p.personId));
      const removed = data.persons.length - persons.length;
      if ('totalCount' in data) {
        return {
          ...data,
          persons,
          count: data.count - removed,
          totalCount: data.totalCount - removed,
        };
      }
      return { ...data, persons, count: data.count - removed };
    });
  }

  /** Apply `map` to the persons array of whichever layer is active. */
  private mapCurrentPersons(
    map: (
      persons: IntelligenceModels.Persons.Person[],
    ) => IntelligenceModels.Persons.Person[],
  ): void {
    this.patchActiveData((data) =>
      'persons' in data ? { ...data, persons: map(data.persons) } : data,
    );
  }

  /**
   * A `groups` array also appears on `VehicleGroups.Response`, so a bare
   * `'groups' in data` check does not narrow to the person-groups shape.
   * Only `PersonGroups.Response` items carry `personsCounter`.
   */
  private isPersonGroupsListData(
    data: IntelligenceResultData,
  ): data is IntelligenceModels.PersonGroups.Response {
    return (
      'groups' in data &&
      (data.groups.length === 0 || 'personsCounter' in data.groups[0])
    );
  }

  /** Prepend a newly created group to a person-groups-list layer (on create success). */
  addPersonGroupToCurrent(
    group: PersonGroupsModel.PersonGroupListDocument,
  ): void {
    this.patchActiveData((data) => {
      if (!this.isPersonGroupsListData(data)) return data;
      return {
        ...data,
        groups: [group, ...data.groups],
        count: data.count + 1,
      };
    });
  }

  /** Rename a group in a person-groups-list layer (on update success). */
  updatePersonGroupNameInCurrent(groupId: string, name: string): void {
    this.patchActiveData((data) => {
      if (!this.isPersonGroupsListData(data)) return data;
      return {
        ...data,
        groups: data.groups.map((g) =>
          g._id === groupId ? { ...g, name } : g,
        ),
      };
    });
  }

  /**
   * Append newly added persons to a members layer whose group is one of
   * `groupIds`, and bump the matching group's counter in a groups-list layer.
   */
  addPersonsToGroupsInCurrent(
    groupIds: string[],
    addedPersons: IntelligenceModels.Persons.Person[],
  ): void {
    const groupIdSet = new Set(groupIds);
    const addedCount = addedPersons.length;
    this.patchActiveData((data) => {
      if (
        'groupId' in data &&
        'persons' in data &&
        groupIdSet.has(data.groupId)
      ) {
        return {
          ...data,
          persons: [...data.persons, ...addedPersons],
          count: data.count + addedCount,
          totalCount: data.totalCount + addedCount,
        };
      }
      if (this.isPersonGroupsListData(data)) {
        return {
          ...data,
          groups: data.groups.map((g) =>
            groupIdSet.has(g._id)
              ? { ...g, personsCounter: g.personsCounter + addedCount }
              : g,
          ),
        };
      }
      return data;
    });
  }

  /** Drop a group from a person-groups-list layer (on delete success). */
  removePersonGroupFromCurrent(groupId: string): void {
    this.patchActiveData((data) => {
      if (!this.isPersonGroupsListData(data)) return data;
      const groups = data.groups.filter((g) => g._id !== groupId);
      if (groups.length === data.groups.length) return data;
      return { ...data, groups, count: Math.max(0, data.count - 1) };
    });
  }

  /** Merge `changes` into a single vehicle (on edit success). Covers both a plain vehicles layer and a group members layer — both shapes carry a `vehicles` array. */
  updateVehicleInCurrent(
    plate: string,
    changes: Partial<IntelligenceModels.Vehicles.Vehicle>,
  ): void {
    this.mapCurrentVehicles((vehicles) =>
      vehicles.map((v) => (v.plate === plate ? { ...v, ...changes } : v)),
    );
  }

  /** Drop vehicles by plate and adjust counts (on delete success). */
  removeVehiclesFromCurrentByPlates(plates: string[]): void {
    const plateSet = new Set(plates);
    this.patchActiveData((data) => {
      if (!('vehicles' in data)) return data;
      const vehicles = data.vehicles.filter((v) => !plateSet.has(v.plate));
      const removed = data.vehicles.length - vehicles.length;
      if ('totalCount' in data) {
        return {
          ...data,
          vehicles,
          count: data.count - removed,
          totalCount: data.totalCount - removed,
        };
      }
      return { ...data, vehicles, count: data.count - removed };
    });
  }

  /** Drop vehicles by id and adjust counts (on remove-from-group success). */
  removeVehiclesFromCurrentByIds(vehicleIds: number[]): void {
    const idSet = new Set(vehicleIds);
    this.patchActiveData((data) => {
      if (!('vehicles' in data)) return data;
      const vehicles = data.vehicles.filter(
        (v) => v.id == null || !idSet.has(v.id),
      );
      const removed = data.vehicles.length - vehicles.length;
      if ('totalCount' in data) {
        return {
          ...data,
          vehicles,
          count: data.count - removed,
          totalCount: data.totalCount - removed,
        };
      }
      return { ...data, vehicles, count: data.count - removed };
    });
  }

  /** Apply `map` to the vehicles array of whichever layer is active. */
  private mapCurrentVehicles(
    map: (
      vehicles: IntelligenceModels.Vehicles.Vehicle[],
    ) => IntelligenceModels.Vehicles.Vehicle[],
  ): void {
    this.patchActiveData((data) =>
      'vehicles' in data ? { ...data, vehicles: map(data.vehicles) } : data,
    );
  }

  /**
   * A `groups` array also appears on `PersonGroups.Response`, so a bare
   * `'groups' in data` check does not narrow to the vehicle-groups shape.
   * Only `VehicleGroups.Response` items carry `vehiclesCounter`.
   */
  private isVehicleGroupsListData(
    data: IntelligenceResultData,
  ): data is IntelligenceModels.VehicleGroups.Response {
    return (
      'groups' in data &&
      (data.groups.length === 0 || 'vehiclesCounter' in data.groups[0])
    );
  }

  /** Prepend a newly created group to a groups-list layer (on create success). */
  addVehicleGroupToCurrent(
    group: VehicleGroupsModel.VehicleGroupListDocument,
  ): void {
    this.patchActiveData((data) => {
      if (!this.isVehicleGroupsListData(data)) return data;
      return {
        ...data,
        groups: [group, ...data.groups],
        count: data.count + 1,
      };
    });
  }

  /** Rename a group in a groups-list layer (on update success). */
  updateVehicleGroupNameInCurrent(groupId: string, name: string): void {
    this.patchActiveData((data) => {
      if (!this.isVehicleGroupsListData(data)) return data;
      return {
        ...data,
        groups: data.groups.map((g) =>
          g._id === groupId ? { ...g, name } : g,
        ),
      };
    });
  }

  /** Drop a group from a groups-list layer (on delete success). */
  removeVehicleGroupFromCurrent(groupId: string): void {
    this.patchActiveData((data) => {
      if (!this.isVehicleGroupsListData(data)) return data;
      const groups = data.groups.filter((g) => g._id !== groupId);
      if (groups.length === data.groups.length) return data;
      return { ...data, groups, count: Math.max(0, data.count - 1) };
    });
  }

  /**
   * Append newly added vehicles to a members layer whose group is one of
   * `groupIds`, and bump the matching group's counter in a groups-list layer.
   */
  addVehiclesToGroupsInCurrent(
    groupIds: string[],
    addedVehicles: IntelligenceModels.Vehicles.Vehicle[],
  ): void {
    const groupIdSet = new Set(groupIds);
    const addedCount = addedVehicles.length;
    this.patchActiveData((data) => {
      if (
        'groupId' in data &&
        'vehicles' in data &&
        groupIdSet.has(data.groupId)
      ) {
        return {
          ...data,
          vehicles: [...data.vehicles, ...addedVehicles],
          count: data.count + addedCount,
          totalCount: data.totalCount + addedCount,
        };
      }
      if (this.isVehicleGroupsListData(data)) {
        return {
          ...data,
          groups: data.groups.map((g) =>
            groupIdSet.has(g._id)
              ? { ...g, vehiclesCounter: g.vehiclesCounter + addedCount }
              : g,
          ),
        };
      }
      return data;
    });
  }

  private removeArchiveFromCurrent(sessionId: string): void {
    this.patchActiveData((data) => {
      if (!this.isArchiveData(data)) return data;
      return {
        ...data,
        archives: data.archives.filter((archive) => archive.sessionId !== sessionId),
        count: Math.max(0, data.count - 1),
      };
    });
  }

  private updateArchiveCaseInCurrent(sessionId: string, caseId: string): void {
    this.patchActiveData((data) => {
      if (!this.isArchiveData(data)) return data;
      return {
        ...data,
        archives: data.archives.map((archive) =>
          archive.sessionId === sessionId ? { ...archive, caseId } : archive,
        ),
      };
    });
  }

  private patchArchiveProgressInCurrent(incoming: Archive.ArchiveDocument): void {
    this.patchActiveData((data) => {
      if (!this.isArchiveData(data)) return data;
      let archiveIndex = incoming.sessionId
        ? data.archives.findIndex((archive) => archive.sessionId === incoming.sessionId)
        : -1;
      if (archiveIndex < 0 && incoming._id) {
        archiveIndex = data.archives.findIndex((archive) => archive._id === incoming._id);
      }

      const updateKey = incoming.sessionId || incoming._id;
      if (!updateKey) return data;
      const incomingUpdateTime = incoming.progressUpdateTime ?? incoming.updatedAt;

      if (archiveIndex >= 0) {
        const current = data.archives[archiveIndex];
        const lastUpdateTime = this.archiveSocketUpdateTimes.get(updateKey) ??
          current.progressUpdateTime ?? current.updatedAt ?? undefined;
        if (incomingUpdateTime != null && lastUpdateTime != null && incomingUpdateTime < lastUpdateTime) {
          return data;
        }
        if (incomingUpdateTime != null) this.archiveSocketUpdateTimes.set(updateKey, incomingUpdateTime);

        const archives = [...data.archives];
        archives[archiveIndex] = {
          ...current,
          ...incoming,
          cameraName: incoming.cameraName || current.cameraName,
        };
        return { ...data, archives };
      }

      if (!this.canPrependArchiveSocketItem(data.filter)) return data;
      if (incomingUpdateTime != null) this.archiveSocketUpdateTimes.set(updateKey, incomingUpdateTime);
      return {
        ...data,
        archives: [incoming, ...data.archives],
        count: data.count + 1,
      };
    });
  }

  private canPrependArchiveSocketItem(filter: IntelligenceModels.Archive.Filter): boolean {
    return !filter.query?.trim() &&
      !filter.cameraName &&
      !filter.cameraIds?.length &&
      !filter.statuses?.length &&
      !filter.types?.length &&
      filter.start == null &&
      filter.end == null;
  }

  private isArchiveData(data: IntelligenceResultData): data is IntelligenceModels.Archive.Response {
    return 'archives' in data && 'filter' in data;
  }

  private activeAddCameraRequest(): IntelligenceModels.CameraProvisioning.AddCameraRequest | null {
    const current = this.current();
    return current?.type === IntelligenceModels.CameraProvisioning.PanelResultType.AddCamera
      ? current.data as IntelligenceModels.CameraProvisioning.AddCameraRequest
      : null;
  }

  private isActiveAddCameraRequest(requestId: string): boolean {
    return this.activeAddCameraRequest()?.requestId === requestId;
  }

  /** Replace the data of the active layer (overlay if shown, else history entry). */
  private patchActiveData(
    update: (data: IntelligenceResultData) => IntelligenceResultData,
  ): void {
    const overlay = this._overlay();
    if (overlay) {
      this._overlay.set({ ...overlay, data: update(overlay.data) });
      return;
    }
    this._history.update((h) => {
      const i = this._index();
      const entry = h[i];
      if (!entry) return h;
      const next = [...h];
      next[i] = {
        ...entry,
        result: { ...entry.result, data: update(entry.result.data) },
      };
      return next;
    });
  }

  // ── Overlay setter (for interactive in-panel drill-downs) ───────────────────

  /**
   * Display `data` in the preview panel WITHOUT adding it to the history.
   * Use this for interactive actions (chart bar drill-down, findByFace, etc.)
   * so that the sequential message↔history mapping is never broken.
   *
   * The overlay is cleared automatically when the user navigates to any
   * history entry or when a new chat-driven tool result arrives.
   */
  setOverlay(
    data: IntelligenceResultData,
    type: IntelligenceResultType,
    label: string,
  ): void {
    this._overlay.set({ type, data });
    this._overlayLabel.set(label);
  }

  /** Dismiss the overlay and fall back to the current history entry. */
  clearOverlay(): void {
    this._overlay.set(null);
    this._overlayLabel.set('');
  }

  /**
   * Dismiss an interactive overlay from the UI. A camera picker is different
   * from a read-only overlay: its tool handler is awaiting an answer, so
   * dismissal must resolve that pending handler with a cancellation.
   */
  dismissOverlay(): void {
    if (
      this.current()?.type ===
      IntelligenceModels.Locations.PanelResultType.PickCamera
    ) {
      this.cancelCameraPick();
      return;
    }

    if (
      this.current()?.type ===
      IntelligenceModels.CameraProvisioning.PanelResultType.AddCamera
    ) {
      this.cancelAddCamera();
      return;
    }

    this.clearOverlay();
  }

  // ── Navigation ──────────────────────────────────────────────────────────────

  navigatePrev(): void {
    this.dismissOverlay();
    if (this.hasPrev()) this._index.update((i) => i - 1);
  }

  navigateNext(): void {
    this.dismissOverlay();
    if (this.hasNext()) this._index.update((i) => i + 1);
  }

  /** Jump directly to the panel entry produced by the Nth user question (0-based). */
  navigateTo(index: number): void {
    this.dismissOverlay();
    const h = this._history();
    if (index >= 0 && index < h.length) {
      this._index.set(index);
    }
  }

  /** Full reset — called when the user starts a new chat or re-enters AI mode. */
  clear(): void {
    this._history.set([]);
    this._index.set(-1);
    this._pendingPrompt.set('');
    this._overlay.set(null);
    this._overlayLabel.set('');
    this._createCustomObjectRequest.set(null);
  }

  private pushEntry(result: IntelligenceResult, label: string): void {
    // A new chat-driven result always dismisses any active overlay.
    this.clearOverlay();
    const entry: IntelligenceResultEntry = {
      result,
      label,
      prompt: this._pendingPrompt(),
    };
    this._history.update((h) => [...h, entry]);
    // Always jump to the newly appended entry regardless of where the user was
    // navigating in the history (avoids pointing to the wrong entry when the
    // user had scrolled back before triggering a new tool call / findByFace).
    this._index.set(this._history().length - 1);
  }
}
